import { Injectable, OnDestroy } from '@angular/core';
import {
  AudioCallTelemetryAck,
  AudioCallTelemetryBatch,
  AudioCallTelemetryEntry,
  audioCallSafeMetrics,
} from '@app/services/audio-call-telemetry.interface';

@Injectable({providedIn: 'root'})
export class AudioCallTelemetryService implements OnDestroy {
  private readonly capacity: number = 500;
  private readonly lifetimeMs: number = 300000;
  private readonly maxAttempts: number = 3;
  private queue: AudioCallTelemetryEntry[] = [];
  private pending: AudioCallTelemetryBatch | null = null;
  private sender: ((batch: AudioCallTelemetryBatch) => Promise<AudioCallTelemetryAck | null>) | null = null;
  private isReady: () => boolean = (): boolean => true;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private flushing: boolean = false;
  private disposed: boolean = false;
  private dropped: number = 0;
  private attempts: number = 0;
  private generation: number = 0;

  ngOnDestroy(): void {
    this.disposed = true;
    this.clear();
    this.sender = null;
  }

  public setSender(
    sender: (batch: AudioCallTelemetryBatch) => Promise<AudioCallTelemetryAck | null>,
    isReady: () => boolean = (): boolean => true,
  ): void {
    this.sender = sender;
    this.isReady = isReady;
  }

  public clear(): void {
    this.generation += 1;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.queue = [];
    this.pending = null;
    this.dropped = 0;
    this.attempts = 0;
    this.flushing = false;
  }

  public record(entry: Omit<AudioCallTelemetryEntry, 'metrics'>, detail?: unknown): void {
    if (this.disposed) return;
    const metrics = audioCallSafeMetrics(detail);
    if (this.dropped > 0) metrics.dropped_events = this.dropped;
    this.queue.push({...entry, metrics});
    if (this.queue.length > this.capacity) {
      this.queue.shift();
      this.dropped += 1;
    }
    this.schedule(1000);
  }

  public async flush(): Promise<void> {
    if (this.disposed || this.flushing || !this.sender) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    const cutoff = Date.now() - this.lifetimeMs;
    this.queue = this.queue.filter((entry): boolean => entry.timestamp >= cutoff);
    if (this.pending && this.pending.entries[0].timestamp < cutoff) {
      this.pending = null;
      this.attempts = 0;
    }
    if (!this.isReady()) {
      if (this.pending || this.queue.length > 0) this.schedule(5000);
      return;
    }
    if (!this.pending && this.queue.length > 0) {
      const entries = this.queue.splice(0, 20);
      this.pending = {version: 1, batch_id: `${entries[0].session_id}:${entries[0].seq}`, entries};
    }
    const batch = this.pending;
    if (!batch) return;
    const generation = this.generation;
    this.flushing = true;
    this.attempts += 1;
    let accepted = false;
    try {
      const ack = await this.sender(batch);
      accepted = ack?.accepted === true && ack.batch_id === batch.batch_id;
      if (accepted && this.pending === batch) this.pending = null;
    } catch {
      // Diagnostics never fail the call. The bounded batch is retried after reconnect.
    } finally {
      if (generation === this.generation) {
        this.flushing = false;
        if (accepted || this.attempts >= this.maxAttempts) {
          if (!accepted) this.dropped += batch.entries.length;
          this.pending = null;
          this.attempts = 0;
        }
        if (this.pending || this.queue.length > 0) this.schedule(accepted ? 1000 : 5000);
      }
    }
  }

  private schedule(delay: number): void {
    if (this.disposed || this.timer || this.flushing) return;
    this.timer = setTimeout((): void => {
      this.timer = null;
      void this.flush();
    }, delay);
  }
}
